import { useEffect, useState } from "react";
import { api, formatNaira } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, GraduationCap, Users, BookOpen, Award, Clock } from "lucide-react";
import { toast } from "sonner";

const CAT_COLORS = { web: "info", design: "purple", business: "aqua", data: "warning", marketing: "success", general: "default" };
const LEVEL_LABEL = { beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced" };

export default function Academy() {
  const [summary, setSummary] = useState({});
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [open, setOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", category: "web", level: "beginner",
    duration_hours: 20, price: 50000, modules_count: 6, status: "draft",
  });
  const [enrollForm, setEnrollForm] = useState({ course_id: "", student_name: "", student_email: "", student_phone: "", payment_status: "pending" });

  const load = () => {
    api.get("/academy/summary").then(r => setSummary(r.data));
    api.get("/academy/courses").then(r => setCourses(r.data));
    api.get("/academy/enrollments").then(r => setEnrollments(r.data));
  };
  useEffect(() => { load(); }, []);

  const createCourse = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/academy/courses", { ...form, price: Number(form.price), duration_hours: Number(form.duration_hours), modules_count: Number(form.modules_count) });
    toast.success("Course created");
    setOpen(false);
    setForm({ title: "", description: "", category: "web", level: "beginner", duration_hours: 20, price: 50000, modules_count: 6, status: "draft" });
    load();
  };

  const enrollStudent = async () => {
    if (!enrollForm.course_id || !enrollForm.student_name || !enrollForm.student_email) return toast.error("Course, name, email required");
    await api.post("/academy/enrollments", enrollForm);
    toast.success("Student enrolled");
    setEnrollOpen(false);
    setEnrollForm({ course_id: "", student_name: "", student_email: "", student_phone: "", payment_status: "pending" });
    load();
  };

  const openEnroll = (course) => {
    setEnrollForm({ ...enrollForm, course_id: course.id });
    setSelectedCourse(course);
    setEnrollOpen(true);
  };

  const KPI = ({ label, value, icon: Icon }) => (
    <div className="kpi-card">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">{label}</div>
        <div className="w-8 h-8 rounded-md bg-brand-aqua-light text-brand-aqua-hover flex items-center justify-center"><Icon className="w-4 h-4" /></div>
      </div>
      <div className="font-heading font-bold text-3xl text-slate-900 dark:text-slate-100 num">{value}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Figure Academy"
        subtitle="Online training school — courses, students, and revenue"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-course-btn">
                <Plus className="w-4 h-4 mr-1" /> New Course
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>New course</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="course-title-input" /></div>
                <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="web">Web / Tech</SelectItem>
                        <SelectItem value="design">Design</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="data">Data</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Level</Label>
                    <Select value={form.level} onValueChange={v => setForm({...form, level: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Duration (h)</Label><Input type="number" value={form.duration_hours} onChange={e => setForm({...form, duration_hours: e.target.value})} /></div>
                  <div><Label>Modules</Label><Input type="number" value={form.modules_count} onChange={e => setForm({...form, modules_count: e.target.value})} /></div>
                  <div><Label>Price (₦)</Label><Input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} /></div>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
              <DialogFooter><Button onClick={createCourse} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-course-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Courses" value={summary.courses_total ?? 0} icon={BookOpen} />
        <KPI label="Published" value={summary.courses_published ?? 0} icon={Award} />
        <KPI label="Students" value={summary.students_total ?? 0} icon={Users} />
        <KPI label="Revenue" value={formatNaira(summary.revenue)} icon={GraduationCap} />
      </div>

      <Tabs defaultValue="courses">
        <TabsList>
          <TabsTrigger value="courses" data-testid="tab-courses"><BookOpen className="w-4 h-4 mr-1.5" />Courses</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students"><Users className="w-4 h-4 mr-1.5" />Students</TabsTrigger>
        </TabsList>

        <TabsContent value="courses" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(c => (
              <div key={c.id} className="kpi-card group" data-testid={`course-card-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-brand-aqua-light text-brand-aqua-hover flex items-center justify-center">
                      <GraduationCap className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-800 dark:text-slate-100">{c.title}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Chip variant={CAT_COLORS[c.category] || "default"}>{c.category}</Chip>
                        <Chip>{LEVEL_LABEL[c.level] || c.level}</Chip>
                      </div>
                    </div>
                  </div>
                  <Chip variant={statusChip(c.status)}>{c.status}</Chip>
                </div>
                {c.description && <div className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-2">{c.description}</div>}
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-3 gap-2 text-xs">
                  <div><div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />Duration</div><div className="font-medium text-slate-900 dark:text-slate-100 num">{c.duration_hours}h</div></div>
                  <div><div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><BookOpen className="w-3 h-3" />Modules</div><div className="font-medium text-slate-900 dark:text-slate-100 num">{c.modules_count}</div></div>
                  <div className="text-right"><div className="text-slate-500 dark:text-slate-400">Price</div><div className="font-heading font-semibold text-brand-aqua-hover num">{formatNaira(c.price)}</div></div>
                </div>
                {c.status === "published" && (
                  <Button size="sm" onClick={() => openEnroll(c)} className="w-full mt-3 bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid={`enroll-btn-${c.id}`}>
                    Enroll student
                  </Button>
                )}
              </div>
            ))}
            {courses.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 text-sm">No courses yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="students" className="mt-4">
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">Student</th>
                  <th className="text-left px-4 py-3">Course</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Payment</th>
                  <th className="text-left px-4 py-3">Progress</th>
                  <th className="text-right px-4 py-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map(e => (
                  <tr key={e.id} className="row">
                    <td className="px-4 py-3"><div className="font-medium text-slate-800 dark:text-slate-100">{e.student_name}</div><div className="text-xs text-slate-500">{e.student_email}</div></td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{e.course_title}</td>
                    <td className="px-4 py-3"><Chip variant={e.status === "completed" ? "success" : e.status === "active" ? "info" : "default"}>{e.status}</Chip></td>
                    <td className="px-4 py-3"><Chip variant={e.payment_status === "paid" ? "success" : e.payment_status === "refunded" ? "danger" : "warning"}>{e.payment_status}</Chip></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${e.progress >= 100 ? "bg-emerald-500" : "bg-brand-aqua"}`} style={{ width: `${e.progress}%` }} />
                        </div>
                        <span className="text-xs num text-slate-500 w-9">{e.progress}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-heading font-semibold num">{formatNaira(e.course_price)}</td>
                  </tr>
                ))}
                {enrollments.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No enrollments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enroll student — {selectedCourse?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input value={enrollForm.student_name} onChange={e => setEnrollForm({...enrollForm, student_name: e.target.value})} data-testid="student-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={enrollForm.student_email} onChange={e => setEnrollForm({...enrollForm, student_email: e.target.value})} /></div>
            <div><Label>Phone (optional)</Label><Input value={enrollForm.student_phone} onChange={e => setEnrollForm({...enrollForm, student_phone: e.target.value})} /></div>
            <div><Label>Payment status</Label>
              <Select value={enrollForm.payment_status} onValueChange={v => setEnrollForm({...enrollForm, payment_status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={enrollStudent} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-enrollment-btn">Enroll</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
